// src/pages/dubbing/DubbingPage.js - FIXED VERSION
import React, { useState } from 'react';
import { Container, Box } from '@mui/material';
import { useToast } from '../../contexts/ToastContext';
import DashboardLayout from '../../components/common/DashboardLayout/DashboardLayout';
import DubbingHeader from '../../components/dubbing/DubbingHeader';
import DubbingTabs from '../../components/dubbing/DubbingTabs';
import JobsList from '../../components/dubbing/JobsList';
import CreateDubModal from '../../components/dubbing/CreateDubModal';
import VideoPreviewModal from '../../components/dubbing/VideoPreviewModal';
import CinematicProcessingOverlay from '../../components/dubbing/CinematicProcessingOverlay';
import { useDubbingJobs } from '../../hooks/useDubbingJobs';
import { useDubbingActions } from '../../hooks/useDubbingActions';

const DubbingPage = () => {
  const { showSuccess } = useToast();
  
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedJobForPreview, setSelectedJobForPreview] = useState(null);
  const [cinematicJob, setCinematicJob] = useState(null);
  const [cinematicOpen, setCinematicOpen] = useState(false);

  // Custom hooks
  const {
    allJobs,
    stats,
    loading,
    error,
    activeJobsCount,
    addJob,
    updateJob,
    removeJob,
    getFilteredJobs,
  } = useDubbingJobs();

  const {
    handlePlay,
    handleDownload,
    handleCancel,
    handleDelete,
    handleRetry,
  } = useDubbingActions({ updateJob, removeJob });

  // Updated tab filters to handle all statuses correctly
  const tabFilters = [
    { 
      label: 'All', 
      filter: null,
      description: 'All jobs'
    },
    { 
      label: 'Processing', 
      filter: 'active',
      description: 'Jobs in progress'
    },
    { 
      label: 'Completed', 
      filter: 'completed',
      description: 'Successfully completed jobs'
    },
    { 
      label: 'Failed', 
      filter: 'failed',
      description: 'Failed jobs'
    },
  ];

  //Enhanced filtering function to handle the 'active' filter
  const getFilteredJobsForTab = (filterType) => {
    if (!filterType) {
      return allJobs; 
    }
    
    if (filterType === 'active') {
      return allJobs.filter(job => {
        const activeStatuses = [
          'pending_approval',
          'approved', 
          'pending',
          'processing'
        ];
        return activeStatuses.includes(job.status);
      });
    }
    
    // For other filters, use the original filtering logic
    return getFilteredJobs(filterType);
  };

  // Handlers
  const handleCreateDub = () => {
    setCreateModalOpen(true);
  };

  const handleDubCreated = (newJob) => {
    addJob(newJob);
    showSuccess('Dubbing started successfully!');
    setActiveTab(1);
    setCinematicJob(newJob);
    setCinematicOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePlayVideo = (job) => {
    const result = handlePlay(job);
    if (result && result.video_url) {
      setSelectedJobForPreview(result);
      setPreviewModalOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewModalOpen(false);
    setSelectedJobForPreview(null);
  };

  // Use the enhanced filtering function
  const filteredJobs = getFilteredJobsForTab(tabFilters[activeTab].filter);

  return (
    <DashboardLayout>
      <Container
        maxWidth={false}
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, md: 4 },
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
        }}
      >
        {/* Header */}
        <DubbingHeader
          stats={stats}
          activeJobsCount={activeJobsCount}
          onCreateDub={handleCreateDub}
        />

        {/* Tabs */}
        <DubbingTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          activeJobsCount={activeJobsCount}
          tabFilters={tabFilters}
          allJobs={allJobs} 
        />

        {/* Jobs List */}
        <Box sx={{ width: '100%' }}>
          <JobsList
            loading={loading}
            error={error}
            jobs={filteredJobs}
            activeTab={activeTab}
            tabFilters={tabFilters}
            onPlay={handlePlayVideo}
            onDownload={handleDownload}
            onCancel={handleCancel}
            onDelete={handleDelete}
            onRetry={handleRetry}
          />
        </Box>
      </Container>

      {/* Create Dub Modal */}
      <CreateDubModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleDubCreated}
      />

      {/* Video Preview Modal */}
      <VideoPreviewModal
        open={previewModalOpen}
        onClose={handleClosePreview}
        job={selectedJobForPreview}
      />

      {/* Cinematic processing overlay */}
      <CinematicProcessingOverlay
        open={cinematicOpen}
        job={cinematicJob}
        onClose={() => setCinematicOpen(false)}
      />
    </DashboardLayout>
  );
};

export default DubbingPage;